import AssignmentTextField from "./AssignmentTextField";
import {
    MAX_MEET_ASSIST_STAFFS,
    PHONE_COUNTRY_CODE,
    emptyMeetAssistStaff,
} from "./assignmentUtils";

/**
 * The people working the meet & assist service — regular or VIP — for this
 * booking.
 *
 * They are not tied to a vehicle: one booking has one set of staff standing in
 * the terminal however many vehicles it runs, so this is shown once, after every
 * vehicle slot, and is saved by the same "Assign" button.
 *
 * How many people a booking needs is the office's call, so rows are added and
 * removed freely; a phone number is optional because a name is enough to hand to
 * the passenger, and the staff themselves can be left for later.
 */
const MeetAssistStaffField = ({ serviceLabel, staffs, isIncomplete = false, onChange }) => {
    const canRemove = staffs.length > 1;

    const patchStaff = (index, patch) =>
        onChange(staffs.map((staff, position) => (position === index ? { ...staff, ...patch } : staff)));

    const removeStaff = (index) => onChange(staffs.filter((_, position) => position !== index));

    const addStaff = () => onChange([...staffs, emptyMeetAssistStaff()]);

    return (
        <div>
            <div className="tw:p-4 tw:bg-[#F9F9F9] tw:border tw:border-[#E8E8E8]">
                <div className="tw:font-hanken-grotesk tw:text-[13px] tw:font-[400] tw:text-[#8D8D8D]">
                    Meet Service Staff
                </div>
                <div className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:leading-[38px] tw:text-[#282828]">
                    {serviceLabel || "Meet & Assist"}
                </div>
            </div>
            <div className="tw:h-px tw:w-full tw:bg-[#E8E8E8]" />

            <div className="tw:px-4 tw:py-6">
                <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-[220px_1fr] tw:md:items-start">
                    <div className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[700] tw:text-[#4E4E4E] tw:md:pt-3">
                        Staff
                    </div>
                    <div className="tw:flex tw:flex-col tw:gap-3">
                        <p className="tw:m-0 tw:font-hanken-grotesk tw:text-[13px] tw:leading-[18px] tw:text-[#8D8D8D]">
                            The staff meeting the passenger in the terminal. They are shared across every vehicle on
                            this booking.
                        </p>

                        {staffs.map((staff, index) => (
                            // Rows have no identity of their own, and reordering is not
                            // possible here, so the position is a stable enough key.
                            <div
                                key={`meet-assist-staff-${index}`}
                                className="tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-[1fr_1fr_auto] tw:md:items-end"
                            >
                                <AssignmentTextField
                                    label={`Staff name #${index + 1}`}
                                    placeholder="e.g. Sato Hiroko"
                                    value={staff.name}
                                    onChange={(value) => patchStaff(index, { name: value })}
                                />
                                <AssignmentTextField
                                    label="Phone number (optional)"
                                    placeholder="80-0000-0000"
                                    prefix={PHONE_COUNTRY_CODE}
                                    value={staff.phone}
                                    onChange={(value) => patchStaff(index, { phone: value })}
                                />
                                {canRemove ? (
                                    <button
                                        type="button"
                                        aria-label={`Remove staff #${index + 1}`}
                                        onClick={() => removeStaff(index)}
                                        className="tw:!h-[52px] tw:!rounded-[5px] tw:!border tw:!border-[#E8E8E8] tw:!bg-white tw:!px-4 tw:!font-hanken-grotesk tw:!text-[13px] tw:!text-[#EA4335]"
                                    >
                                        Remove
                                    </button>
                                ) : (
                                    // Keeps the two fields the same width whether or not the
                                    // row can be removed.
                                    <span className="tw:hidden tw:md:block tw:h-[52px]" />
                                )}
                            </div>
                        ))}

                        {isIncomplete ? (
                            <p className="tw:m-0 tw:font-hanken-grotesk tw:text-[13px] tw:leading-[18px] tw:!text-[#EA4335]">
                                Every staff member with a phone number needs a name — clear the row or fill the name in.
                            </p>
                        ) : null}

                        {staffs.length < MAX_MEET_ASSIST_STAFFS ? (
                            <div>
                                <button
                                    type="button"
                                    onClick={addStaff}
                                    className="tw:!rounded-full tw:!border tw:!border-[#DBBE78] tw:!bg-white tw:!px-4 tw:!py-2 tw:!font-hanken-grotesk tw:!text-[13px] tw:!leading-[16px] tw:!text-[#A3780F]"
                                >
                                    + Add staff
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MeetAssistStaffField;
